import React, { useState } from 'react';

import { Box, Button, Grid, Paper, TextField, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';

import { KingdomSearchResult, Province } from '../../@types/KingdomResponse';
import { useIntelState } from '../../context/IntelState';
import RestUtilities from '../../services/RestUtilities';
import { StripedDataGrid } from '../Intel/components/StripedDataGrid';

const staticCols: GridColDef[] = [
  {
    field: 'location',
    headerName: 'Location',
    sortable: false,
    width: 60,
  },
  {
    field: 'kingdomName',
    headerName: 'Kingdom',
  },
  {
    field: 'warStatus',
    headerName: 'Status',
    width: 70,
  },
  {
    field: 'warLocation',
    headerName: 'War',
    width: 50,
  },
  {
    field: 'land',
    headerName: 'Acres',
    width: 80,
    type: 'number',
  },
  {
    field: 'netWorth',
    headerName: 'NW',
    width: 90,
    type: 'number',
  },
  {
    field: 'honor',
    headerName: 'Honor',
    width: 70,
    type: 'number',
  },
  {
    field: 'provinces',
    headerName: '#',
    width: 30,
    type: 'number',
  },
];

const KdSearch: React.FC = () => {
  const { intelState } = useIntelState();
  const apiRef = useGridApiRef();
  const [dataSet, setDataSet] = useState<KingdomSearchResult[] | undefined>(
    undefined
  );
  const [races, setRaces] = useState<string[]>([]);
  const [cols, setCols] = useState<GridColDef[] | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const [acres, setAcres] = useState(
    intelState.ownData?.provinces.reduce(
      (a: number, b: Province) => a + b.land,
      0
    )
  );
  const [networth, setNetworth] = useState(
    intelState.ownData?.provinces.reduce(
      (a: number, b: Province) => a + b.networth,
      0
    )
  );
  const [provinces, setProvinces] = useState(
    intelState.ownData?.provinces.length ?? 0
  );

  const refreshData = () => {
    const getData = async () => {
      const response = await RestUtilities.post(`Kingdom/v1/Search`, {
        server: intelState.server,
        acres: acres,
        networth: networth,
        provinces: provinces,
      });
      if (response.ok) {
        const results: KingdomSearchResult[] = response.content;
        setDataSet(results);
        const newRaces: string[] = [];
        results.forEach((x) => {
          Object.keys(x.races).forEach((x) => {
            if (newRaces.includes(x)) return;
            newRaces.push(x);
          });
        });
        const cols: GridColDef[] = staticCols;
        newRaces.forEach((x) => {
          cols.push({
            field: x.slice(0, 2),
            headerName: x.slice(0, 2),
            maxWidth: 30,
            type: 'number',
            valueGetter: (value, row) => {
              return row.races[x];
            },
          } as GridColDef);
        });
        setCols(cols);
        setRaces(newRaces);
        setFetching(false);
      }
    };
    if (!fetching) {
      setFetching(true);
      getData();
    }
  };

  return (
    <Box
      sx={{
        my: 2,
      }}
    >
      <Typography variant='h4' component='div'>
        Kingdom Search
      </Typography>
      <Typography variant='body1' component='div'>
        The search will return results within +/- 30% of the selected
        acres/networth values and within +/- 3 provinces count.
      </Typography>
      <Grid container spacing={2} mt={2} columns={8}>
        <Grid>
          <TextField
            variant='outlined'
            fullWidth
            size='small'
            type='number'
            label='Acres'
            sx={{ minWidth: '100px' }}
            slotProps={{
              input: {
                inputProps: {
                  min: 1,
                  max: 1000000,
                },
              },
            }}
            value={acres}
            onChange={(e) => setAcres(Number(e.target.value))}
          />
        </Grid>
        <Grid>
          <TextField
            variant='outlined'
            fullWidth
            size='small'
            type='number'
            label='Networth'
            sx={{ minWidth: '130px' }}
            slotProps={{
              input: {
                inputProps: {
                  min: 1,
                  max: 1000000,
                },
              },
            }}
            value={networth}
            onChange={(e) => setNetworth(Number(e.target.value))}
          />
        </Grid>
        <Grid>
          <TextField
            variant='outlined'
            fullWidth
            size='small'
            type='number'
            label='Provinces'
            sx={{ minWidth: '100px' }}
            slotProps={{
              input: {
                inputProps: {
                  min: 1,
                  max: 25,
                },
              },
            }}
            value={provinces}
            onChange={(e) => setProvinces(Number(e.target.value))}
          />
        </Grid>
        <Grid>
          <Button variant='outlined' onClick={refreshData}>
            Search
          </Button>
        </Grid>
      </Grid>
      {dataSet?.length && cols && (
        <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
          <StripedDataGrid
            rows={dataSet}
            apiRef={apiRef}
            getDetailPanelHeight={({ row }) => 'auto'}
            getRowId={(row) => row.location}
            columns={cols}
            density='compact'
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            loading={intelState.loading}
            hideFooterPagination
            rowHeight={40}
          />
        </Paper>
      )}
    </Box>
  );
};

export default KdSearch;
